import { inject } from 'vue'
import { get } from 'lodash'
import { RequestService } from './RequestService'

/**
 * TransformLearningProgressService — frontend client for the transformLearningGateway
 * Cloud Function. Firestore reads stay on the SDK (TransformLearningService); this
 * covers the gateway-only operations: progress writes (blocked for clients by rules)
 * and resolving the private Vimeo embed URL (keeps the access token server-side).
 *
 * Must be instantiated within a component setup (inject requires an active
 * instance), mirroring ComplianceAuditLogService.
 */
export const TransformLearningProgressService = () => {
  const { post, simpleGet, handleErrors } = RequestService()
  const functionsBaseUrl = inject('functionsBaseUrl')

  /**
   * Upsert progress for a single lesson.
   * @param {Object} progress - { lessonId, courseId, uniquePercentWatched?, percentComplete?, completed? }
   * @returns {Promise<boolean>} true when the server confirmed the write.
   */
  async function saveLessonProgress(progress) {
    try {
      const response = await post(
        'saveLessonProgress',
        `${functionsBaseUrl}/transformLearningGateway/api/v1/lesson-progress`,
        progress
      )
      return response?.data?.success === true
    } catch (error) {
      handleErrors('TransformLearningProgressService.saveLessonProgress', error)
      throw error
    }
  }

  /**
   * Submit a quiz for server-side scoring. The backend resolves the correct option
   * ids, calculates the score, persists the result, and returns the score + pass flag.
   * @param {Object} payload - { lessonId, courseId, answers: { [questionId]: selectedOptionId } }
   * @returns {Promise<{scorePercent:number, passed:boolean, passThresholdPercent:number, correct:number, total:number}>}
   */
  async function submitQuiz(payload) {
    const url = `${functionsBaseUrl}/transformLearningGateway/api/v1/quiz-submissions`
    try {
      const response = await post(
        'submitQuiz',
        url,
        payload
      )
      return response?.data?.data ?? null
    } catch (error) {
      handleErrors('TransformLearningProgressService.submitQuiz', error)
      throw error
    }
  }

  /**
   * Reset a quiz lesson's progress so the user can retake it.
   * Clears answers, score, completion flag, and returns to question 0.
   * @param {Object} payload - { lessonId, courseId }
   * @returns {Promise<boolean>} true when the server confirmed the write.
   */
  async function restartQuiz(payload) {
    try {
      const response = await post(
        'restartQuiz',
        `${functionsBaseUrl}/transformLearningGateway/api/v1/lesson-progress`,
        {
          lessonId: payload.lessonId,
          courseId: payload.courseId,
          currentQuestionIndex: 0,
          draftAnswers: {},
          completed: false,
          scorePercent: null,
          percentComplete: 0,
          correctCount: null,
          totalCount: null,
          passed: false,
        }
      )
      return response?.data?.success === true
    } catch (error) {
      handleErrors('TransformLearningProgressService.restartQuiz', error)
      throw error
    }
  }

  /**
   * Resolve the embeddable Vimeo URL (with private hash) for a video id.
   * @param {string|number} vimeoVideoId
   * @returns {Promise<{embedUrl: string, hash: string|null}|null>}
   */
  async function getVideoEmbed(vimeoVideoId) {
    try {
      const response = await simpleGet(
        'getVideoEmbed',
        `${functionsBaseUrl}/transformLearningGateway/api/v1/videos/${vimeoVideoId}/embed`
      )
      return get(response, 'data.data', null)
    } catch (error) {
      handleErrors('TransformLearningProgressService.getVideoEmbed', error)
      throw error
    }
  }

  /**
   * Fetch the quiz questions for an assessment WITHOUT the answer key. Scoring is
   * server-side, so correctOptionIds are never sent to the client.
   * @param {string} assessmentId
   * @returns {Promise<Array<{id:string, position:number, type:string, prompt:string, options:Array}>>}
   */
  async function getQuizQuestions(assessmentId) {
    try {
      const response = await simpleGet(
        'getQuizQuestions',
        `${functionsBaseUrl}/transformLearningGateway/api/v1/assessments/${assessmentId}/questions`
      )
      return get(response, 'data.data', []) ?? []
    } catch (error) {
      handleErrors('TransformLearningProgressService.getQuizQuestions', error)
      throw error
    }
  }

  /**
   * Fetch the full breakdown of a COMPLETED quiz attempt: every question with its
   * options, the answer key, and the user's own selections, plus the score summary.
   * The answer key is only exposed by this endpoint and only after completion.
   * @param {string} lessonId
   * @param {string|null} [owner] Optional target user id (platform-admin only, enforced server-side).
   * @returns {Promise<Object|null>} breakdown payload, or null if unavailable.
   */
  async function getQuizResultBreakdown(lessonId, owner = null) {
    try {
      const base = `${functionsBaseUrl}/transformLearningGateway/api/v1/lessons/${lessonId}/quiz-result-breakdown`
      const url = owner ? `${base}?owner=${encodeURIComponent(owner)}` : base
      const response = await simpleGet('getQuizResultBreakdown', url)
      return get(response, 'data.data', null)
    } catch (error) {
      handleErrors('TransformLearningProgressService.getQuizResultBreakdown', error)
      throw error
    }
  }

  /**
   * Fetch a user's bcst_lesson_progress docs for a course via the gateway. Only used by
   * the admin "view-as" flow: self progress is read client-side (TransformLearningService),
   * but another user's docs are unreadable under rules and must come through the Admin SDK.
   * @param {string} courseId
   * @param {string} owner Target user id (platform-admin only, enforced server-side).
   * @returns {Promise<{progress: Array, targetUser: Object|null, ownerResolved: boolean}|null>}
   */
  async function getCourseProgress(courseId, owner) {
    try {
      const base = `${functionsBaseUrl}/transformLearningGateway/api/v1/courses/${courseId}/progress`
      const url = owner ? `${base}?owner=${encodeURIComponent(owner)}` : base
      // rethrow: a refusal comes back as 200 with ownerResolved: false, so a thrown error
      // here is a real transport/gateway failure. Swallowing it would return null and the
      // caller would report it as "you do not have access to this user".
      const response = await simpleGet('getCourseProgress', url, { rethrow: true })
      return get(response, 'data.data', null)
    } catch (error) {
      handleErrors('TransformLearningProgressService.getCourseProgress', error)
      throw error
    }
  }

  return { saveLessonProgress, submitQuiz, restartQuiz, getVideoEmbed, getQuizQuestions, getQuizResultBreakdown, getCourseProgress }
}
